import React, { useState } from 'react';
import { CoachClientList } from '../dashboard/CoachClientList';
import { DashboardPage } from '../../pages/DashboardPage';
import { NutritionOverviewPage } from '../../pages/NutritionOverviewPage';
import { GoalsProgressPage } from '../../pages/GoalsProgressPage';
import { ReportsPage } from '../../pages/ReportsPage';
import { ClientProfilePage } from '../../pages/ClientProfilePage';
import { TherapeuticTargetsPage } from '../../pages/TherapeuticTargetsPage';
import { ClientActivityPage } from '../../pages/ClientActivityPage';
import { DailyFoodEntriesPage } from '../../pages/DailyFoodEntriesPage';
import { AICoachAssistant } from './AICoachAssistant';
import { ChatbotTestPage } from '../../pages/ChatbotTestPage';
import { MessagingPage } from '../../pages/MessagingPage';
import { MealPlanningPage } from '../../pages/MealPlanningPage';
import { CasesPage } from '../../pages/CasesPage';

interface CoachInterfaceProps {
  user: any;
  onLogout: () => void;
}

export const CoachInterface: React.FC<CoachInterfaceProps> = ({ user, onLogout }) => {
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [selectedView, setSelectedView] = useState<string>('dashboard');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);
  const [isAIAssistantMinimized, setIsAIAssistantMinimized] = useState<boolean>(false);

  const handleClientSelect = (clientId: number, clientName: string) => {
    setSelectedClient({ id: clientId, full_name: clientName });
  };

  const views = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊', component: DashboardPage },
    { id: 'nutrition', label: 'Nutrition', icon: '🥗', component: NutritionOverviewPage },
    { id: 'goals', label: 'Goals', icon: '🎯', component: GoalsProgressPage },
    { id: 'targets', label: 'Targets', icon: '🎯', component: TherapeuticTargetsPage },
    { id: 'meal-planning', label: 'Meal Plan', icon: '📅', component: MealPlanningPage },
    { id: 'cases', label: 'Cases', icon: '📋', component: CasesPage },
    { id: 'profile', label: 'Profile', icon: '👤', component: ClientProfilePage },
    { id: 'daily-foods', label: 'Daily Foods', icon: '🍽️', component: DailyFoodEntriesPage },
    { id: 'messages', label: 'Messages', icon: '💬', component: MessagingPage },
    { id: 'reports', label: 'Reports', icon: '📈', component: ReportsPage },
    { id: 'chatbot', label: 'AI Chatbot', icon: '🤖', component: ChatbotTestPage },
    { id: 'client-activity', label: 'Activity', icon: '📅', component: ClientActivityPage },
  ];

  const CurrentViewComponent = views.find(v => v.id === selectedView)?.component || DashboardPage;

  return (
    <div className="h-screen flex bg-gray-100">
      {/* Client Sidebar */}
      <div className={`${isSidebarCollapsed ? 'w-0' : 'w-80'} bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ease-in-out overflow-hidden`}>
        {/* Coach Header — co-branded for Ankorium-bridged coaches */}
        <div
          className="p-6"
          style={
            user?.coachBranding?.accent_color
              ? { background: user.coachBranding.accent_color, color: '#ffffff' }
              : { background: 'linear-gradient(to right, #2563eb, #1d4ed8)' }
          }
        >
          <div className="flex items-center gap-3">
            {user?.coachBranding?.logo_url && (
              <img
                src={user.coachBranding.logo_url}
                alt={user.coachBranding.brand_name || 'Brand logo'}
                className="h-10 w-10 rounded-lg object-contain bg-white p-1"
              />
            )}
            <h1 className="text-xl font-bold text-white">
              {user?.coachBranding?.brand_name || 'Coach Dashboard'}
            </h1>
          </div>
          <p className="text-white/80 text-sm mt-1">
            Welcome, {user?.full_name || user?.email || 'Coach'}
          </p>
          <button
            onClick={onLogout}
            className="mt-3 text-xs text-white/70 hover:text-white transition-colors"
          >
            Sign Out
          </button>
        </div>

        {/* Client List */}
        <div className="flex-1 overflow-y-auto">
          <CoachClientList
            onClientSelect={handleClientSelect}
            selectedClientId={selectedClient?.id}
          />
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex relative">
        <div className="flex-1 flex flex-col">
        {/* Sidebar Toggle Button */}
        <button
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          className="absolute left-4 top-4 z-10 bg-white border border-gray-300 rounded-md p-2 shadow-sm hover:bg-gray-50 transition-colors"
          title={isSidebarCollapsed ? "Show client list" : "Hide client list"}
        >
          <svg
            className={`w-5 h-5 text-gray-600 transition-transform duration-300 ${isSidebarCollapsed ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>

        {selectedClient ? (
          <>
            {/* Client Header */}
            <div className="bg-white border-b border-gray-200 p-4 pl-16">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {selectedClient.full_name}
                  </h2>
                  <p className="text-gray-600 text-sm">Client Dashboard View</p>
                </div>
                
                {/* View Navigation */}
                <div className="flex space-x-1 mr-36">
                  {views.map((view) => (
                    <button
                      key={view.id}
                      onClick={() => setSelectedView(view.id)}
                      className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        selectedView === view.id
                          ? view.id === 'profile'
                            ? 'bg-indigo-100 text-indigo-700 ring-2 ring-indigo-500'
                            : 'bg-blue-100 text-blue-700'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                      }`}
                    >
                      <span className="mr-1">{view.icon}</span>
                      {view.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Client Dashboard Content */}
            <div className="flex-1 overflow-y-auto">
              <CurrentViewComponent
                user={selectedView === 'messages' ? user : selectedClient}
                coachMode={true}
                clientId={selectedClient.id}
                clientName={selectedClient.full_name}
              />
            </div>
          </>
        ) : (
          /* No Client Selected */
          <div className="flex-1 flex items-center justify-center pl-16">
            <div className="text-center">
              <div className="text-6xl mb-6">👥</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Select a Client
              </h2>
              <p className="text-gray-600 max-w-md">
                {isSidebarCollapsed ? (
                  <>Click the <span className="font-semibold">{'<<'}</span> button to show the client list</>
                ) : (
                  <>Choose a client from the sidebar to view their complete nutrition dashboard
                  with all professional features and reports.</>
                )}
              </p>
            </div>
          </div>
        )}
        </div>

        {/* AI Coach Assistant Panel */}
        {!isAIAssistantMinimized && (
          <div className="w-96 h-full">
            <AICoachAssistant
              clientId={selectedClient?.id || null}
              clientName={selectedClient?.full_name || null}
              isMinimized={isAIAssistantMinimized}
              onToggleMinimize={() => setIsAIAssistantMinimized(true)}
            />
          </div>
        )}

        {/* AI Assistant Minimize/Expand Button */}
        {isAIAssistantMinimized && (
          <button
            onClick={() => setIsAIAssistantMinimized(false)}
            className="absolute right-4 top-4 z-10 bg-purple-600 text-white border border-purple-700 rounded-md px-3 py-2 shadow-md hover:bg-purple-700 transition-all flex items-center gap-2"
            title="Show AI Assistant"
          >
            <span className="text-sm font-medium">🤖 AI Assistant</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};